import { formatPercentage } from '@/domain/common/format'
import { sortByAPY, sortByUsdValue } from '@/domain/common/sorters'
import { OpenDialogFunction } from '@/domain/hooks/useDialog'
import { useCardSolverConfigs, useStoreHydrated } from '@/domain/state'
import { CardRouteBadge } from '@/features/card/components/CardRouteBadge'
import { getPooledBorrowCardRouteBadge } from '@/features/card/utils/cardSolverRouteMatching'
import { borrowDialogConfig } from '@/features/dialogs/borrow/BorrowDialog'
import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { ApyTooltip } from '@/ui/molecules/apy-tooltip/ApyTooltip'
import { ActionsCell } from '@/ui/molecules/data-table/components/ActionsCell'
import { CompactValueCell } from '@/ui/molecules/data-table/components/CompactValueCell'
import { TokenWithLogo } from '@/ui/molecules/data-table/components/TokenWithLogo'
import { ResponsiveDataTable } from '@/ui/organisms/responsive-data-table/ResponsiveDataTable'
import { ApyWithRewardsCell } from '@/features/my-portfolio/components/ApyWithRewardsCell'
import { Borrow } from '../../logic/assets'
import { ColumnDefinition } from '@/ui/molecules/data-table/types'
import { useMemo } from 'react'
import { usePointsData } from '@/domain/points/pointsData/usePointsData'
import { EModeCategoryId } from '@/domain/e-mode/types'
import { getDisplayToken } from '@/domain/hooks/useDisplayToken'
import { useAccount, useChainId } from 'wagmi'
import {
  applyExtraIncentivesUSDCToBorrowAPY,
  useExtraIncentivesUSDC,
} from '@/domain/hooks/useExtraIncentivesUSDC'

export interface BorrowTableProps {
  assets: Borrow[]
  eModeCategoryId: EModeCategoryId
  openDialog: OpenDialogFunction
}

export function BorrowTable({ assets, eModeCategoryId, openDialog }: BorrowTableProps) {
  const { address } = useAccount()
  const { getByAddress, error: pointsDataError, isLoading: isPointsDataLoading } = usePointsData()
  const { byAddress: cardSolverConfigsByAddress } = useCardSolverConfigs()
  const isStoreHydrated = useStoreHydrated()
  const hasPointsError = !isPointsDataLoading && !!pointsDataError?.message
  const chainId = useChainId()
  const extraIncentivesUSDC = useExtraIncentivesUSDC()
  const cardSolverConfigs = useMemo(
    () => (isStoreHydrated && address ? Object.values(cardSolverConfigsByAddress[address.toLowerCase()] ?? {}) : []),
    [address, cardSolverConfigsByAddress, isStoreHydrated],
  )

  const filteredAssets = useMemo(
    () =>
      assets.filter(({ eModeCategoryId: assetEModeCategoryId }) => {
        return eModeCategoryId === 0 || eModeCategoryId === assetEModeCategoryId
      }),
    [assets, eModeCategoryId],
  )

  const columnDefinition = useMemo<{ [key: string]: ColumnDefinition<Borrow> }>(() => {
    return {
      symbol: {
        header: 'Assets',
        renderCell: ({ token, reserveStatus, isCombinedBalance }) => {
          const displayToken = getDisplayToken(chainId, token)
          const cardRouteBadge = getPooledBorrowCardRouteBadge(cardSolverConfigs, token.address)

          return (
            <div className="flex items-center gap-2">
              <TokenWithLogo
                token={displayToken}
                reserveStatus={reserveStatus}
                isCombinedBalance={isCombinedBalance}
                hideSymbol
              />
              <CardRouteBadge badge={cardRouteBadge} />
            </div>
          )
        },
      },
      inWallet: {
        header: 'Available',
        sortable: true,
        sortingFn: (a, b) => sortByUsdValue(a.original, b.original, 'available'),
        headerAlign: 'right',
        renderCell: ({ token, available }, mobileViewOptions) => (
          <CompactValueCell token={token} value={available} mobileViewOptions={mobileViewOptions} hideEmpty />
        ),
      },
      deposit: {
        header: 'Your borrow',
        sortable: true,
        sortingFn: (a, b) => sortByUsdValue(a.original, b.original, 'debt'),
        headerAlign: 'right',
        showOnMobile: true,
        renderCell: ({ token, debt }, mobileViewOptions) => (
          <CompactValueCell token={token} value={debt} mobileViewOptions={mobileViewOptions} hideEmpty />
        ),
      },
      apy: {
        header: <ApyTooltip variant="borrow">APY</ApyTooltip>,
        headerAlign: 'right',
        sortable: true,
        showOnMobile: true,
        sortingFn: (a, b) =>
          sortByAPY(
            applyExtraIncentivesUSDCToBorrowAPY(a.original.borrowAPY, a.original.token.symbol, extraIncentivesUSDC).totalAPY,
            applyExtraIncentivesUSDCToBorrowAPY(b.original.borrowAPY, b.original.token.symbol, extraIncentivesUSDC).totalAPY,
          ),
        renderCell: ({ borrowAPY, token }, mobileViewOptions) => {
          const points = getByAddress({
            type: 'pooled',
            action: 'borrow',
            address: token.address,
          })

          const { baseAPY, bonusAPY, totalAPY } = applyExtraIncentivesUSDCToBorrowAPY(
            borrowAPY,
            token.symbol,
            extraIncentivesUSDC,
          )
          const additionalAPY = bonusAPY ? formatPercentage(bonusAPY) : undefined
          return (
            <ApyWithRewardsCell
              action="borrow"
              asset={token.symbol}
              apy={totalAPY}
              points={points}
              pointsError={hasPointsError}
              pointsLoading={isPointsDataLoading}
              mobileViewOptions={mobileViewOptions}
              extraIncentives={
                token.symbol === 'USDC' && extraIncentivesUSDC ? extraIncentivesUSDC.description : undefined
              }
              baseAPYDisplay={baseAPY ? formatPercentage(baseAPY) : undefined}
              totalAPYDisplay={totalAPY ? formatPercentage(totalAPY) : undefined}
              additionalAPY={additionalAPY}
              showAdditionalAPYInCell={false}
              gradient={!!bonusAPY}
            />
          )
        },
      },
      actions: {
        header: '',
        renderCell: ({ token, reserveStatus, available }) => {
          return (
            <ActionsCell>
              <Button
                size="sm"
                className="w-full md:w-fit"
                onClick={() => {
                  openDialog(borrowDialogConfig, { token })
                }}
                disabled={reserveStatus === 'frozen' || available.isZero()}
              >
                Borrow
              </Button>
            </ActionsCell>
          )
        },
      },
    }
  }, [
    openDialog,
    getByAddress,
    hasPointsError,
    isPointsDataLoading,
    chainId,
    extraIncentivesUSDC,
    cardSolverConfigs,
  ])

  return (
    <Panel collapsibleOptions={{ collapsible: true, collapsibleAbove: 'md' }} className="bg-panel md:px-3">
      <Panel.Header>
        <Panel.Title className="text-xl md:px-3" gradient>
          Available to borrow
        </Panel.Title>
      </Panel.Header>

      <Panel.Content>
        <ResponsiveDataTable
          gridTemplateColumnsClassName="grid-cols-[4fr_repeat(3,_3fr)_5fr]"
          columnDefinition={columnDefinition}
          data={filteredAssets}
        />
      </Panel.Content>
    </Panel>
  )
}
